import {CopyPasteState} from '@mintdata-wsp/common/state/ui/visualDesigner/CopyPasteState';
import {ImportState} from '@mintdata-wsp/common/state/ui/visualDesigner/ImportState';
import {
    IMeshCellNoteSnapshot,
    MeshCellNote,
    MeshCellNoteId
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellNote';
import {
    IMeshCellSettingsSnapshot,
    MeshCellSettings,
    MeshCellSettingsId
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {IMeshStateSnapshot, MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {
    ISheetSettingsSnapshot,
    SheetSettings
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetSettings';
import {SheetSettingsId} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetSettingsTypes';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {action, observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';
import {DesignStateFromJsonCtx} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';

export interface ISpreadsheetViewStateSnapshot
{
    meshStates:{[sheetId:string]:IMeshStateSnapshot};
    sheetSettings:{[sheetSettingsId:string]:ISheetSettingsSnapshot};
    cellSettings:{[meshCellSettingsId:string]:IMeshCellSettingsSnapshot};
    cellNotes:{[meshCellNoteId:string]:IMeshCellNoteSnapshot};
}

export class SpreadsheetViewState
{
    @observable
    public meshStates:Map<SheetId, MeshState> = new Map();

    @observable
    public sheetSettings:Map<SheetSettingsId, SheetSettings> = new Map();

    @observable
    public cellSettings:Map<MeshCellSettingsId, MeshCellSettings> = new Map();

    @observable
    public cellNotes:Map<MeshCellNoteId, MeshCellNote> = new Map();

    @observable
    public focusedSheetId:SheetId = null;

    @observable
    public copyPasteState:CopyPasteState = null;

    @observable
    public importState:ImportState = new ImportState();

    @observable
    public areNamedRangesVisible:boolean = true;

    private constructor()
    {
    }

    public static create(sheets:Map<SheetId, AnySheet>):SpreadsheetViewState
    {
        const spreadsheetViewState:SpreadsheetViewState = new SpreadsheetViewState();

        const meshStates:Map<SheetId, MeshState> = new Map();
        const sheetSettingsMap:Map<SheetSettingsId, SheetSettings> = new Map();

        sheets.forEach((sheet:AnySheet) =>
        {
            const meshState:MeshState = MeshState.create(sheet);
            const sheetSettings:SheetSettings = SheetSettings.create(sheet.id, sheet.id, null);

            meshStates.set(meshState.sheet.id, meshState);
            sheetSettingsMap.set(sheetSettings.id, sheetSettings);
        });

        spreadsheetViewState.meshStates = meshStates;
        spreadsheetViewState.sheetSettings = sheetSettingsMap;

        return spreadsheetViewState;
    }

    public getMeshStateBySheetId(sheetId:SheetId):MeshState
    {
        return this.meshStates.get(sheetId);
    }

    public getSheetSettingsById(sheetSettingsId:SheetSettingsId):SheetSettings
    {
        return (sheetSettingsId && this.sheetSettings.get(sheetSettingsId)) || null;
    }

    public getCellSettings(cellSettingsId:MeshCellSettingsId):MeshCellSettings
    {
        return this.cellSettings.get(cellSettingsId);
    }

    public getCellNote(cellNoteId:MeshCellNoteId):MeshCellNote
    {
        return this.cellNotes.get(cellNoteId);
    }

    @action
    public addMeshState(meshState:MeshState):void
    {
        this.meshStates.set(meshState.sheet.id, meshState);
    }

    @action
    public deleteMeshState(sheetId:SheetId):void
    {
        const meshState:MeshState = this.getMeshStateBySheetId(sheetId);

        if( meshState )
            this.meshStates.delete(sheetId);
        else
            mk.error(area.Slot, 'MeshState is not found. SheetId:', sheetId);
    }

    @action
    public addSheetSettings(sheetSettings:SheetSettings):void
    {
        this.sheetSettings.set(sheetSettings.id, sheetSettings);
    }

    @action
    public deleteSheetSettings(sheetSettingsId:SheetSettingsId):void
    {
        const sheetSettings:SheetSettings = this.getSheetSettingsById(sheetSettingsId);

        if( sheetSettings )
            this.sheetSettings.delete(sheetSettingsId);
    }

    @action
    public addCellSettings(cellSettings:MeshCellSettings):void
    {
        this.cellSettings.set(cellSettings.id, cellSettings);
    }

    @action
    public deleteCellSettings(cellSettingsId:string):void
    {
        this.cellSettings.delete(cellSettingsId);
    }

    @action
    public addCellNote(cellNote:MeshCellNote):void
    {
        this.cellNotes.set(cellNote.id, cellNote);
    }

    @action
    public removeCellNote(cellNoteId:MeshCellNoteId):boolean
    {
        return this.cellNotes.delete(cellNoteId);
    }

    @action
    public setFocusedSheetId(sheetId:SheetId):void
    {
        this.focusedSheetId = sheetId;
    }

    @action
    public setCopyPasteState(copyPasteState:CopyPasteState):void
    {
        this.copyPasteState = copyPasteState;
    }

    public static toJson:ITransformer<SpreadsheetViewState, ISpreadsheetViewStateSnapshot> = createTransformer((spreadsheetViewState:SpreadsheetViewState) =>
    {
        return {
            meshStates: Array.from(spreadsheetViewState.meshStates.entries())
                .reduce((result, [meshStateId, meshState]) =>
                {
                    result[meshStateId] = MeshState.toJson(meshState);
                    return result;
                }, {}),
            sheetSettings: Array.from(spreadsheetViewState.sheetSettings.entries())
                .reduce((result, [sheetSettingsId, sheetSettings]) =>
                {
                    result[sheetSettingsId] = SheetSettings.toJson(sheetSettings);
                    return result;
                }, {}),
            cellSettings: Array.from(spreadsheetViewState.cellSettings.entries())
                .reduce((result, [cellSettingsId, cellSettings]) =>
                {
                    result[cellSettingsId] = MeshCellSettings.toJson(cellSettings);
                    return result;
                }, {}),
            cellNotes: Array.from(spreadsheetViewState.cellNotes.entries())
                .reduce((result, [cellNoteId, cellNote]) =>
                {
                    result[cellNoteId] = MeshCellNote.toJson(cellNote);
                    return result;
                }, {})
        };
    });

    public static fromJson(json:ISpreadsheetViewStateSnapshot,
                           sheets:Map<SheetId, AnySheet>,
                           currentState?:SpreadsheetViewState,
                           currentJson?:ISpreadsheetViewStateSnapshot,
                           ctx?:DesignStateFromJsonCtx):SpreadsheetViewState
    {
        if( !currentState )
            return SpreadsheetViewState.fromJsonDeep(json, sheets);

        if( !currentJson )
            currentJson = SpreadsheetViewState.toJson(currentState);

        const spreadsheetViewState:SpreadsheetViewState = new SpreadsheetViewState();

        if( json.meshStates === currentJson.meshStates )
            spreadsheetViewState.meshStates = currentState.meshStates;
        else
        {
            const meshStates:Map<SheetId, MeshState> = new Map();

            Object.entries(json.meshStates)
                .forEach(([meshStateId, meshState]) =>
                {
                    const meshStateCurrentState:MeshState = currentState.meshStates.get(meshStateId);
                    const meshStateCurrentJson:IMeshStateSnapshot = currentJson.meshStates[meshStateId];

                    if( json.meshStates[meshStateId] === currentJson.meshStates[meshStateId] )
                        meshStates.set(meshStateId, meshStateCurrentState);
                    else
                        meshStates.set(meshStateId, MeshState.fromJson(
                            meshState,
                            sheets,
                            meshStateCurrentState,
                            meshStateCurrentJson,
                            ctx
                        ));
                });

            spreadsheetViewState.meshStates = meshStates;
        }

        if( json.sheetSettings === currentJson.sheetSettings )
            spreadsheetViewState.sheetSettings = currentState.sheetSettings;
        else
        {
            const sheetSettingsList:Map<SheetSettingsId, SheetSettings> = new Map();

            Object.entries(json.sheetSettings)
                .forEach(([sheetSettingsId, sheetSettings]) =>
                {
                    const sheetSettingsCurrentState:SheetSettings = currentState.sheetSettings.get(sheetSettingsId);
                    const sheetSettingsCurrentJson:ISheetSettingsSnapshot = currentJson.sheetSettings[sheetSettingsId];

                    if( json.sheetSettings[sheetSettingsId] === currentJson.sheetSettings[sheetSettingsId] )
                        sheetSettingsList.set(sheetSettingsId, sheetSettingsCurrentState);
                    else
                        sheetSettingsList.set(sheetSettingsId, SheetSettings.fromJson(
                            sheetSettings,
                            sheetSettingsCurrentState,
                            sheetSettingsCurrentJson,
                            ctx
                        ));
                });

            spreadsheetViewState.sheetSettings = sheetSettingsList;
        }

        if( json.cellSettings === currentJson.cellSettings )
            spreadsheetViewState.cellSettings = currentState.cellSettings;
        else
        {
            const cellSettingsList:Map<MeshCellSettingsId, MeshCellSettings> = new Map();

            Object.entries(json.cellSettings)
                .forEach(([cellSettingsId, cellSettings]) =>
                {
                    const cellSettingsCurrentState:MeshCellSettings = currentState.cellSettings.get(cellSettingsId);
                    const cellSettingsCurrentJson:IMeshCellSettingsSnapshot = currentState.cellSettings[cellSettingsId];

                    if( json.cellSettings[cellSettingsId] === currentJson.cellSettings[cellSettingsId] )
                        cellSettingsList.set(cellSettingsId, cellSettingsCurrentState);
                    else
                        cellSettingsList.set(cellSettingsId, MeshCellSettings.fromJson(
                            cellSettings,
                            cellSettingsCurrentState,
                            cellSettingsCurrentJson,
                            ctx
                        ));
                });

            spreadsheetViewState.cellSettings = cellSettingsList;
        }

        if( json.cellNotes === currentJson.cellNotes )
            spreadsheetViewState.cellNotes = currentState.cellNotes;
        else
        {
            const cellNotes:Map<MeshCellNoteId, MeshCellNote> = new Map();

            Object.entries(json.cellNotes)
                .forEach(([cellNoteId, cellNote]) =>
                {
                    const cellNotesCurrentState:MeshCellNote = currentState.cellNotes.get(cellNoteId);
                    const cellNotesCurrentJson:IMeshCellNoteSnapshot = currentState.cellNotes[cellNoteId];

                    if( json.cellNotes[cellNoteId] === currentJson.cellNotes[cellNoteId] )
                        cellNotes.set(cellNoteId, cellNotesCurrentState);
                    else
                        cellNotes.set(cellNoteId, MeshCellNote.fromJson(
                            cellNote,
                            cellNotesCurrentState,
                            cellNotesCurrentJson,
                            ctx
                        ));
                });

            spreadsheetViewState.cellNotes = cellNotes;
        }

        SpreadsheetViewState._restoreUiState(spreadsheetViewState, sheets, currentState);

        return spreadsheetViewState;
    }

    public static fromJsonDeep(json:ISpreadsheetViewStateSnapshot, sheets:Map<SheetId, AnySheet>):SpreadsheetViewState
    {
        const spreadsheetViewState:SpreadsheetViewState = new SpreadsheetViewState();

        const meshStates:Map<SheetId, MeshState> = new Map();
        const sheetSettingsList:Map<SheetSettingsId, SheetSettings> = new Map();
        const cellSettingsList:Map<MeshCellSettingsId, MeshCellSettings> = new Map();
        const cellNotes:Map<MeshCellNoteId, MeshCellNote> = new Map();

        Object.entries(json.meshStates)
            .forEach(([meshStateId, meshState]) => (
                meshStates.set(meshStateId, MeshState.fromJsonDeep(meshState, sheets))
            ));

        Object.entries(json.sheetSettings)
            .forEach(([sheetSettingsId, sheetSettings]) => (
                sheetSettingsList.set(sheetSettingsId, SheetSettings.fromJsonDeep(sheetSettings))
            ));

        Object.entries(json.cellSettings)
            .forEach(([cellSettingsId, cellSettings]) => (
                cellSettingsList.set(cellSettingsId, MeshCellSettings.fromJsonDeep(cellSettings))
            ));

        Object.entries(json.cellNotes)
            .forEach(([cellNoteId, cellNote]) => (
                cellNotes.set(cellNoteId, MeshCellNote.fromJsonDeep(cellNote))
            ));

        spreadsheetViewState.meshStates = meshStates;
        spreadsheetViewState.sheetSettings = sheetSettingsList;
        spreadsheetViewState.cellSettings = cellSettingsList;
        spreadsheetViewState.cellNotes = cellNotes;

        return spreadsheetViewState;
    }

    private static _restoreUiState(state:SpreadsheetViewState, sheets:Map<SheetId, AnySheet>, currentState:SpreadsheetViewState):void
    {
        state.focusedSheetId = currentState.focusedSheetId && sheets.get(currentState.focusedSheetId)
            ? currentState.focusedSheetId
            : Array.from(sheets.values())[0].id;
    }
}
